import {LatLngLiteral} from 'leaflet';

/** WGS84 mean Earth radius (m); local equirectangular approximation at box center. */
const MEAN_EARTH_RADIUS_M = 6371008.8;

/** |cos(lat)| floor to avoid division blow-up near the poles (GroundOverlays are rarely there). */
const MIN_COS_LAT = 1e-8;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function toDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/** Longitude normalized to [-180, 180]. */
export function normalizeLng180(lng: number): number {
  let x = lng % 360;
  if (x > 180) x -= 360;
  if (x < -180) x += 360;
  return x;
}

/** Shortest signed longitude difference from origin to target (degrees). */
export function shortestLngDeltaDeg(originLng: number, targetLng: number): number {
  const a = normalizeLng180(originLng);
  const b = normalizeLng180(targetLng);
  let d = b - a;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
}

/**
 * Center longitude for a KML LatLonBox west/east edge.
 * When the box crosses the antimeridian, KML uses west > east; unwrap east by +360° before averaging.
 */
export function centerLngFromWestEast(west: number, east: number): number {
  let e = east;
  const w = west;
  if (e < w) {
    e += 360;
  }
  return normalizeLng180((w + e) / 2);
}

type Enu = {east: number; north: number};

function cosLat0(centerLat: number): number {
  return Math.max(Math.abs(Math.cos(toRad(centerLat))), MIN_COS_LAT);
}

/** East/north meters from center on local tangent plane (equirectangular at center latitude). */
function enuFromLatLng(centerLat: number, centerLng: number, lat: number, lng: number): Enu {
  const dφ = toRad(lat - centerLat);
  const dλ = toRad(shortestLngDeltaDeg(centerLng, lng));
  const c = cosLat0(centerLat);
  return {
    east: MEAN_EARTH_RADIUS_M * c * dλ,
    north: MEAN_EARTH_RADIUS_M * dφ
  };
}

function latLngFromEnu(centerLat: number, centerLng: number, east: number, north: number): LatLngLiteral {
  const c = cosLat0(centerLat);
  const dφ = north / MEAN_EARTH_RADIUS_M;
  const dλ = east / (MEAN_EARTH_RADIUS_M * c);
  const lat = centerLat + toDeg(dφ);
  const lng = normalizeLng180(centerLng + toDeg(dλ));
  return {lat, lng};
}

/** CCW rotation in the local east–north plane (matches KML rotation: CCW from north). */
function rotateEnu(east: number, north: number, angleRad: number): Enu {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  return {
    east: east * cos - north * sin,
    north: east * sin + north * cos
  };
}

export type LatLonBoxCorners = {
  topLeft: LatLngLiteral;
  topRight: LatLngLiteral;
  bottomLeft: LatLngLiteral;
};

/**
 * GroundOverlay LatLonBox corners after rotation about the box center.
 * Uses meter-based offsets at mean center latitude, then rotates in the local ENU plane.
 */
export function cornersFromLatLonBox(params: {
  north: number;
  south: number;
  east: number;
  west: number;
  rotationDeg: number;
}): LatLonBoxCorners {
  const {north, south, east, west} = params;
  const rotationDeg = Number.isFinite(params.rotationDeg) ? params.rotationDeg : 0;
  const rotation = (rotationDeg * Math.PI) / 180;

  const centerLat = (north + south) / 2;
  const centerLng = centerLngFromWestEast(west, east);

  const corner = (lat: number, lng: number): LatLngLiteral => {
    const {east: e, north: n} = enuFromLatLng(centerLat, centerLng, lat, lng);
    const r = rotateEnu(e, n, rotation);
    return latLngFromEnu(centerLat, centerLng, r.east, r.north);
  };

  return {
    topLeft: corner(north, west),
    topRight: corner(north, east),
    bottomLeft: corner(south, west)
  };
}
